import { BadgeCheck } from 'lucide-react';
import { PageHero } from '@/components/layout/PageHero';
import { ArrowIcon, ButtonLink } from '@/components/ui/Button';
import { SectionIntro } from '@/components/ui/SectionIntro';
import { Locations } from '@/features/site/home/Locations';
import { Seo } from '@/features/site/Seo';
import { useSite } from '@/features/site/SiteProvider';
import { CapabilitiesSection, InquirySection, ProcessSection, ProductionInfoStrip } from '@/features/site/sections';

export default function AboutPage() {
  const { about } = useSite().content;
  const [lead, ...rest] = about.paragraphs;

  return (
    <>
      <Seo title="About Us" path="/about" description={lead?.slice(0, 300)} />
      <PageHero
        crumbs={[{ label: 'About' }]}
        eyebrow={about.eyebrow || 'About us'}
        title={about.heading}
        intro={lead}
        photo="garmentWorkshop"
        actions={
          <ButtonLink to="#inquiry" variant="accent" size="lg" icon={<ArrowIcon />}>
            Request a Quote
          </ButtonLink>
        }
      >
        {about.established.label && <p className="mt-8 text-[13px] font-semibold tracking-[0.14em] text-muted uppercase">{about.established.label}</p>}
      </PageHero>

      <section aria-labelledby="model-title" className="section-y bg-white">
        <div className="container-page grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionIntro id="model-title" eyebrow="How we work" title={about.cardHeadline || 'Made to order for your brand.'} size="md" />
            {rest.map((p) => (
              <p key={p.slice(0, 40)} className="mt-6 text-[16px] leading-[1.75] text-muted">
                {p}
              </p>
            ))}
            {about.leadTime && (
              <p className="mt-6 text-[15px] text-ink-soft">
                Typical bulk lead time: <strong className="font-semibold text-primary">{about.leadTime}</strong>
              </p>
            )}
          </div>
          <div className="grid gap-px self-start overflow-hidden rounded-[8px] border border-line bg-line sm:grid-cols-2 lg:col-span-7">
            {about.highlights.map((h) => (
              <div key={h.title} className="bg-white p-7">
                <h3 className="font-display text-[19px] font-bold tracking-tight text-primary">{h.title}</h3>
                <p className="mt-3 text-[15px] leading-[1.7] text-muted">{h.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Locations />

      <section aria-labelledby="quality-title" className="section-y bg-surface">
        <div className="container-page grid gap-12 lg:grid-cols-12 lg:gap-16">
          <SectionIntro className="lg:col-span-5" id="quality-title" eyebrow="Quality assurance" title={about.quality.heading} intro={about.quality.intro} size="md" />
          <ol className="lg:col-span-7">
            {about.quality.points.map((p, i) => (
              <li key={p} className="flex gap-6 border-t border-line-strong/70 py-5 last:border-b">
                <span className="font-display text-[14px] font-bold text-accent-dark tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                <span className="text-[16px] text-ink-soft">{p}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {about.certifications.length > 0 && (
        <section aria-labelledby="certs-title" className="border-b border-line bg-white py-14">
          <div className="container-page">
            <h2 id="certs-title" className="eyebrow font-sans text-muted">
              Registrations &amp; memberships
            </h2>
            <ul className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {about.certifications.map((c) => (
                <li key={c.name} className="flex gap-3">
                  <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden />
                  <div>
                    <p className="font-semibold text-primary">{c.name}</p>
                    {c.detail && <p className="text-[14px] text-muted">{c.detail}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <ProcessSection />
      <CapabilitiesSection />
      <ProductionInfoStrip />
      <InquirySection />
    </>
  );
}
